import { call, put, takeLatest, select } from 'redux-saga/effects';
import apiRequest from '@libs/apiRequest';
import { setSnack } from 'utilities/redux.actions';
import * as actionTypes from './redux.actionTypes';
import * as actions from './redux.actions';
import * as selectors from './redux.selectors';
import { API_ENDPOINTS, getErrorMessage } from './common';

// Fetch all goals with pagination, filters, and sorting
function* fetchGoalsSaga({ payload = {} }) {
    try {
        // Get current state if no payload provided
        const currentFilters = yield select(selectors.filtersSelector);
        const currentPagination = yield select(selectors.paginationSelector);
        const currentSort = yield select(selectors.sortSelector);

        const params = {
            limit: payload.limit || currentPagination.limit,
            offset: payload.offset || currentPagination.offset,
            'sort-by': payload.sortBy || currentSort.sortBy,
            'sort-descending': payload.sortDescending !== undefined ? payload.sortDescending : currentSort.sortDescending,
            search: payload.search !== undefined ? payload.search : currentFilters.search,
            complexity: payload.complexity !== undefined ? payload.complexity : currentFilters.complexity,
            departmentId: payload.departmentId !== undefined ? payload.departmentId : currentFilters.departmentId
        };

        const response = yield call(apiRequest, {
            url: API_ENDPOINTS.GET_ALL,
            method: 'GET',
            params
        });

        yield put(actions.fetchGoalsSuccess(response.data));
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to fetch goals');
        yield put(actions.fetchGoalsFailure(errorMessage));
        yield put(setSnack({ message: errorMessage, type: 'error' }));
    }
}

// Fetch goal by ID
function* fetchGoalByIdSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.GET_BY_ID}/${payload.id}`,
            method: 'GET'
        });

        yield put(actions.fetchGoalByIdSuccess(response.data));
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to fetch goal details');
        yield put(actions.fetchGoalByIdFailure(errorMessage));
        yield put(setSnack({ message: errorMessage, type: 'error' }));
    }
}

// Create goal
function* createGoalSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: API_ENDPOINTS.CREATE,
            method: 'POST',
            data: JSON.stringify(payload)
        });

        yield put(actions.createGoalSuccess(response.data));
        yield put(actions.closeGoalDialog());
        yield put(setSnack({ message: 'Goal created successfully', type: 'success' }));
        // Refresh the goal list
        yield put(actions.fetchGoals());
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to create goal');
        yield put(actions.createGoalFailure(errorMessage));
        yield put(setSnack({ message: errorMessage, type: 'error' }));
    }
}

// Update goal
function* updateGoalSaga({ payload }) {
    try {
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.UPDATE}/${payload.id}`,
            method: 'PUT',
            data: JSON.stringify(payload.data)
        });

        yield put(actions.updateGoalSuccess(response.data));
        yield put(actions.closeGoalDialog());
        yield put(setSnack({ message: 'Goal updated successfully', type: 'success' }));
        // Refresh the goal list
        yield put(actions.fetchGoals());
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to update goal');
        yield put(actions.updateGoalFailure(errorMessage));
        yield put(setSnack({ message: errorMessage, type: 'error' }));
    }
}

// Delete goal
function* deleteGoalSaga({ payload }) {
    try {
        yield call(apiRequest, {
            url: `${API_ENDPOINTS.DELETE}/${payload.id}`,
            method: 'DELETE'
        });

        yield put(actions.deleteGoalSuccess(payload.id));
        yield put(actions.closeDeleteDialog());
        yield put(setSnack({ message: 'Goal deleted successfully', type: 'success' }));
        // Refresh the goal list
        yield put(actions.fetchGoals());
    } catch (error) {
        // Check if it's a 422 error with usage details
        if (error?.response?.status === 422 && error?.response?.data?.usageDetails) {
            // Dispatch failure to stop loading state
            const errorMessage = getErrorMessage(error, 'Failed to delete goal');
            yield put(actions.deleteGoalFailure(errorMessage));
            
            // Close delete dialog
            yield put(actions.closeDeleteDialog());
            
            // Ensure message is a string
            const message = typeof error.response.data.message === 'string' 
                ? error.response.data.message 
                : 'Cannot delete goal - it is currently in use.';
            
            // Open goal usage dialog with the usage data
            yield put(actions.openGoalUsageDialog({
                message,
                usageDetails: error.response.data.usageDetails,
                suggestion: typeof error.response.data.suggestion === 'string' ? error.response.data.suggestion : null
            }));
        } else {
            // Regular error handling
            const errorMessage = getErrorMessage(error, 'Failed to delete goal');
            yield put(actions.deleteGoalFailure(errorMessage));
            yield put(setSnack({ message: errorMessage, type: 'error' }));
        }
    }
}

// Fetch departments
function* fetchDepartmentsSaga() {
    try {
        const response = yield call(apiRequest, {
            url: API_ENDPOINTS.GET_DEPARTMENTS,
            method: 'GET'
        });
        
        // Extract the array from response.data.data (nested structure)
        const departmentsArray = response.data?.data || [];

        yield put(actions.fetchDepartmentsSuccess(departmentsArray));
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to fetch departments');
        yield put(actions.fetchDepartmentsFailure(errorMessage));
        // Silently fail - departments are not critical
    }
}

// Watcher saga
export default function* goalSaga() {
    yield takeLatest(actionTypes.FETCH_GOALS, fetchGoalsSaga);
    yield takeLatest(actionTypes.FETCH_GOAL_BY_ID, fetchGoalByIdSaga);
    yield takeLatest(actionTypes.CREATE_GOAL, createGoalSaga);
    yield takeLatest(actionTypes.UPDATE_GOAL, updateGoalSaga);
    yield takeLatest(actionTypes.DELETE_GOAL, deleteGoalSaga);
    yield takeLatest(actionTypes.FETCH_DEPARTMENTS, fetchDepartmentsSaga);
    yield takeLatest(actionTypes.SET_FILTERS, fetchGoalsSaga);
    yield takeLatest(actionTypes.SET_PAGINATION, fetchGoalsSaga);
    yield takeLatest(actionTypes.SET_SORT, fetchGoalsSaga);
}
